import React, { useCallback, useMemo, useState } from 'react';
import { Box, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { FinalActionType } from 'store/enums/BallotReviewTypes';
import { Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  finalActionTypeLabel,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';

const FinalActionMotion: React.FunctionComponent = () => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const [type, setType] = useState<FinalActionType | undefined>();
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `final-action-motion`,
      title: 'Action',
      statement: type ? finalActionTypeLabel[type] : '',
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [type, safeBy, safeSecond, discussion]);

  const handleClearMotion = useCallback(() => {
    setType(undefined);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  return (
    <GridContainer>
      <GridItem>
        <FormControl>
          <FormLabel>Motion</FormLabel>
          <RadioGroup
            name="final-action-type-radio-group"
            value={type ?? ''}
            onChange={(event) => setType(event.target.value as FinalActionType)}
          >
            <FormControlLabel
              value={FinalActionType.Passed}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: finalActionTypeLabel[FinalActionType.Passed] }}
                />
              }
            />
            <FormControlLabel
              value={FinalActionType.PassedWithEditorialChanges}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: finalActionTypeLabel[FinalActionType.PassedWithEditorialChanges] }}
                />
              }
            />
            <FormControlLabel
              value={FinalActionType.PassedWithTechnicalChanges}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: finalActionTypeLabel[FinalActionType.PassedWithTechnicalChanges] }}
                />
              }
            />
            <FormControlLabel
              value={FinalActionType.FailedAndReturned}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: finalActionTypeLabel[FinalActionType.FailedAndReturned] }}
                />
              }
            />
            <FormControlLabel
              value={FinalActionType.FailedAndDiscontinued}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: finalActionTypeLabel[FinalActionType.FailedAndDiscontinued] }}
                />
              }
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="final-action-discussion-editor"
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          onClearMotion={handleClearMotion}
          showVotingRuleSelector={false}
          clearFlags={{
            clearDocumentLineItem: true,
            clearReject: true,
            clearTechnicalIssue: true,
            clearComment: true,
            clearIpCheck: true,
          }}
        />
      </GridItem>
    </GridContainer>
  );
};

export default FinalActionMotion;
